import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  BrowserStitchMessage,
  BrowserStitchState,
  BrowserViewport,
} from '@repo/contracts/browser-protocol';
import {
  BROWSER_CLOSE_BUSY,
  BROWSER_CLOSE_SESSION_ENDED,
  BROWSER_CLOSE_UNAUTHORIZED,
} from '@repo/contracts/browser-protocol';

import { logger } from '../lib/logger';
import { createMockTransport } from './mock-transport';
import type { BrowserTransport, FailureKind } from './transport';
import { shouldMockWorker } from './transport';
import { createWsTransport } from './ws-transport';

const log = logger.child({ module: 'browser-session' });

/** Overall session state. `live` is the only one that renders the page. */
export type BrowseStatus = 'connecting' | 'ended' | 'failed' | 'live';

/** Re-exported so consumers import one module, not two. Defined in transport.ts
 *  because a transport reports one (e.g. a 401 minting a ticket). */
export type { FailureKind };

/** Why a live session ended on its own. */
export type EndReason = 'closed' | 'idle';

/** The page inside the browser, which fails independently of the session. */
export type PageStatus = 'error' | 'loading' | 'ready';

/**
 * A capture as the worker just produced it, before it is stored.
 *
 * Not the board's model: what the board renders is `CanvasCapture` from the
 * API, because captures are server state (CLAUDE.md > Frontend state). This
 * type exists only to hand the bundle from the socket to the upload mutation.
 */
export interface CapturedShot {
  /** base64 PNG, exactly as it came off the wire. */
  image: string;
  pageUrl: string;
  /** Which viewport it was taken at — the same union the API stores. */
  device: DeviceKey;
  /**
   * The worker's element inventory for this shot, when it took one.
   *
   * Kept rather than dropped because it is what lets a finding name the
   * element it is about ("Learn more") instead of every finding being
   * page-level — and it cannot be recovered once the session ends.
   */
  dom: Record<string, unknown> | null;
  width: number;
  height: number;
  dpr: number;
  fullPage: boolean;
}

/**
 * The viewports a page can be walked at. `mobile: true` is the worker's touch
 * emulation flag, not a screen-size claim — a tablet gets it for the same
 * reason a phone does: hover-only affordances and :hover-revealed menus behave
 * differently under touch, and judging them without it judges a page nobody
 * sees.
 */
export const DEVICES = {
  desktop: { height: 800, mobile: false, width: 1280 },
  tablet: { height: 1112, mobile: true, width: 834 },
  mobile: { height: 844, mobile: true, width: 390 },
} satisfies Record<string, BrowserViewport>;

export type DeviceKey = keyof typeof DEVICES;

/** Base64 → data URL. Frames are JPEG; captures never go through here. */
function imageSrc(base64: string): string {
  const mime = base64.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';
  return `data:${mime};base64,${base64}`;
}

function failureFor(code: number): FailureKind {
  if (code === BROWSER_CLOSE_UNAUTHORIZED) return 'unauthorized';
  if (code === BROWSER_CLOSE_BUSY) return 'busy';
  return 'unreachable';
}

interface UseBrowserSessionOptions {
  /** The URL to open as soon as the worker is ready. */
  initialUrl: string;
  /**
   * Called with each finished capture. The hook does not keep them: they are
   * uploaded and read back as server state, so holding a second copy here would
   * be two sources of truth for the same rows.
   */
  onCaptured: (shot: CapturedShot) => void;
}

/**
 * Owns one browser worker session for the browse route.
 *
 * Two failure axes on purpose, because they need different UI and different
 * recovery: the **session** can fail (no instance, unauthorized, crash — the
 * whole screen is gone, you retry the connection) while the **page** can fail
 * inside a perfectly healthy session (bad URL, blocked host, timeout — the
 * browser is still there, you retry the navigation). The worker's protocol
 * makes the same distinction: an `error` message never ends a session, only
 * `closed` does.
 */
export function useBrowserSession({
  initialUrl,
  onCaptured,
}: UseBrowserSessionOptions) {
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<BrowseStatus>('connecting');
  const [failure, setFailure] = useState<FailureKind | null>(null);
  const [endReason, setEndReason] = useState<EndReason | null>(null);

  const [frame, setFrame] = useState<string | null>(null);
  const [url, setUrl] = useState(initialUrl);
  const [pageStatus, setPageStatus] = useState<PageStatus>('loading');
  const [pageError, setPageError] = useState<string | null>(null);

  const [device, setDevice] = useState<DeviceKey>('desktop');
  const [viewport, setViewport] = useState<BrowserViewport>(DEVICES.desktop);

  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  /**
   * When the page last changed anything on screen.
   *
   * Chromium only emits a screencast frame on visual change, so silence here
   * means the page has stopped moving — which is the difference between a
   * screen that has finished animating and one caught mid-count. Measured on
   * docpulse.com: its counters were still climbing 1.2s after scrolling into
   * view and settled by 2.6s, and nothing but the frames says which.
   */
  const lastFrameAtRef = useRef(0);

  /** How long the page has been visually still, in ms. */
  const quietFor = useCallback(
    () => (lastFrameAtRef.current ? Date.now() - lastFrameAtRef.current : 0),
    [],
  );

  /** The live values `capture` decides on — see its own note. */
  const capturingRef = useRef(false);
  const pageStatusRef = useRef<PageStatus>('loading');

  /** Always through here, never `setPageStatus` directly: a ref that drifts
   *  from the state it mirrors is worse than not having one. */
  const applyPageStatus = useCallback((next: PageStatus) => {
    pageStatusRef.current = next;
    // The quiet clock restarts with the page. Without this, stillness from
    // *before* a navigation counts as stillness after it — and since
    // about:blank is perfectly still, the first screen of a walk read as
    // "settled" a tenth of a second after the page committed, before anything
    // had painted.
    lastFrameAtRef.current = Date.now();
    setPageStatus(next);
  }, []);

  // Held in a ref so a changing callback identity can't tear down the socket.
  const onCapturedRef = useRef(onCaptured);
  onCapturedRef.current = onCaptured;

  const transportRef = useRef<BrowserTransport | null>(null);
  const targetRef = useRef(initialUrl);
  const readyRef = useRef(false);
  const captureIdRef = useRef(0);

  /**
   * Stitch steps waiting on the worker, by requestId. A socket has no
   * request/response pairing of its own, and unlike a capture these are
   * awaited — the walk cannot shoot a tile before the page has scrolled.
   */
  const stitchRequestsRef = useRef(
    new Map<
      string,
      {
        reject: (err: Error) => void;
        resolve: (state: BrowserStitchState) => void;
      }
    >(),
  );
  const stitchIdRef = useRef(0);

  // Read through refs inside the connect effect below, so switching device
  // doesn't tear down and reconnect the session.
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;
  const deviceRef = useRef(device);
  deviceRef.current = device;

  const send = useCallback<BrowserTransport['send']>((message) => {
    transportRef.current?.send(message);
  }, []);

  useEffect(() => {
    readyRef.current = false;
    setStatus('connecting');
    setFailure(null);
    setEndReason(null);
    setFrame(null);
    applyPageStatus('loading');
    setPageError(null);

    const create = shouldMockWorker() ? createMockTransport : createWsTransport;

    const transport = create({
      handlers: {
        onClose: ({ code }) => {
          if (code === BROWSER_CLOSE_SESSION_ENDED) {
            setStatus('ended');
            setEndReason('idle');
            return;
          }
          // A close *after* ready is a crash/disconnect, not a provisioning
          // failure — the user had a working browser a second ago, so the
          // screen says so instead of "we couldn't get you one".
          if (readyRef.current) {
            setStatus('failed');
            setFailure('crashed');
            return;
          }
          setStatus('failed');
          setFailure(failureFor(code));
        },

        onFailure: (message, kind) => {
          log.warn({ kind, message }, 'browser transport failed');
          setStatus('failed');
          setFailure(kind ?? 'unreachable');
        },

        onMessage: (message) => {
          switch (message.type) {
            case 'ready': {
              readyRef.current = true;
              setStatus('live');
              setViewport(message.session.viewport);

              // Only resize when the worker booted at a different size than the
              // one selected. A redundant `viewport` looks harmless and is not:
              // the worker restarts its screencast on every viewport change, and
              // a restart that lands mid-navigation emits one blank pre-paint
              // frame and then nothing — a static page produces no further
              // compositor frames, so the live view stays white forever.
              const wanted = viewportRef.current;
              const booted = message.session.viewport;
              if (
                wanted.width !== booted.width ||
                wanted.height !== booted.height ||
                wanted.mobile !== booted.mobile
              ) {
                transport.send({ ...wanted, type: 'viewport' });
              }

              // The navigate sent before `ready` is answered with an error by a
              // real worker (it has no session yet), which is why pre-ready
              // errors are ignored. This is the one that actually runs.
              transport.send({ type: 'navigate', url: targetRef.current });
              return;
            }
            case 'frame':
              lastFrameAtRef.current = Date.now();
              setFrame(imageSrc(message.data));
              return;
            case 'url':
              // Chrome's "This site can't be reached" page is not a page that
              // loaded; treating it as one would walk and store Chrome's error
              // screen. The worker reports the failure as an `error`.
              if (message.url.startsWith('chrome-error:')) return;
              setUrl(message.url);
              targetRef.current = message.url;
              applyPageStatus('ready');
              setPageError(null);
              return;
            case 'viewport':
              setViewport(message.viewport);
              return;
            case 'captured': {
              capturingRef.current = false;
              setCapturing(false);
              onCapturedRef.current({
                device: deviceRef.current,
                dom: (message.bundle.dom ?? null) as Record<
                  string,
                  unknown
                > | null,
                dpr: message.bundle.dpr,
                fullPage: message.bundle.fullPage,
                height: message.bundle.imgH,
                image: message.bundle.image,
                pageUrl: targetRef.current,
                width: message.bundle.imgW,
              });
              return;
            }
            case 'stitch': {
              const pending = stitchRequestsRef.current.get(message.requestId);
              stitchRequestsRef.current.delete(message.requestId);
              pending?.resolve(message.state);
              return;
            }
            case 'error': {
              // A stitch step failing is the walk's problem, not the page's:
              // it falls back to a single screen. Settled before the pre-ready
              // check so a waiting promise can never be left hanging.
              const stitch = message.requestId
                ? stitchRequestsRef.current.get(message.requestId)
                : undefined;
              if (stitch && message.requestId) {
                stitchRequestsRef.current.delete(message.requestId);
                stitch.reject(new Error(message.message));
                return;
              }
              // A worker that predates stitching answers "Unknown message
              // type" with no requestId to pair it by. Without this the walk
              // sat out the whole prepare timeout — a minute on a banner that
              // never moved — before falling back to one screen.
              if (
                !message.requestId &&
                message.message.startsWith('Unknown message type: stitch')
              ) {
                const waiting = [...stitchRequestsRef.current.values()];
                stitchRequestsRef.current.clear();
                waiting.forEach(({ reject }) => {
                  reject(new Error(message.message));
                });
                return;
              }
              // Pre-ready errors are the worker rejecting a command it hasn't
              // booted for yet — expected, not shown.
              if (!readyRef.current) return;
              if (message.requestId) {
                capturingRef.current = false;
                setCapturing(false);
                setCaptureError(message.message);
                return;
              }
              applyPageStatus('error');
              setPageError(message.message);
              return;
            }
            case 'closed': {
              setStatus('ended');
              setEndReason(
                message.reason === 'idle_timeout' ? 'idle' : 'closed',
              );
              return;
            }
          }
        },
      },
    });

    transportRef.current = transport;
    // Sent before `ready` deliberately: the mock worker keys its simulated
    // failures off this URL, and a real worker rejects it (no session yet) —
    // harmless, and it means one code path boots both. The `ready` handler
    // above re-sends the one that counts. No viewport is sent here: the worker
    // boots at its own default and is only resized if that differs.
    transport.send({ type: 'navigate', url: targetRef.current });

    const stitchRequests = stitchRequestsRef.current;
    return () => {
      transport.close();
      transportRef.current = null;
      // Nothing will ever answer these now.
      stitchRequests.forEach(({ reject }) => {
        reject(new Error('the browser session closed'));
      });
      stitchRequests.clear();
    };
    // `attempt` is the retry key: bumping it tears the transport down and
    // builds a fresh one. `applyPageStatus` is stable and only listed to
    // satisfy the rule — it can never be the reason a session reconnects.
  }, [applyPageStatus, attempt]);

  /**
   * Whether the live view is actually on screen — false with the tab hidden OR
   * with the canvas board open. The worker pauses its screencast on `visible:
   * false`, and at --concurrency=1 an instance encoding frames nobody is looking
   * at is the single most wasteful thing this client can do; the canvas is where
   * users spend most of their time, so it is the common case, not an edge one.
   */
  const liveViewShownRef = useRef(true);

  const pushVisibility = useCallback(() => {
    send({
      type: 'visible',
      visible: liveViewShownRef.current && !document.hidden,
    });
  }, [send]);

  const setLiveViewShown = useCallback(
    (shown: boolean) => {
      liveViewShownRef.current = shown;
      pushVisibility();
    },
    [pushVisibility],
  );

  useEffect(() => {
    document.addEventListener('visibilitychange', pushVisibility);
    return () => {
      document.removeEventListener('visibilitychange', pushVisibility);
    };
  }, [pushVisibility]);

  const navigate = useCallback(
    (next: string) => {
      targetRef.current = next;
      setUrl(next);
      applyPageStatus('loading');
      setPageError(null);
      send({ type: 'navigate', url: next });
    },
    [applyPageStatus, send],
  );

  const reload = useCallback(() => {
    applyPageStatus('loading');
    setPageError(null);
    send({ action: 'reload', type: 'navigate' });
  }, [applyPageStatus, send]);

  const goBack = useCallback(() => {
    applyPageStatus('loading');
    send({ action: 'back', type: 'navigate' });
  }, [applyPageStatus, send]);

  const goForward = useCallback(() => {
    applyPageStatus('loading');
    send({ action: 'forward', type: 'navigate' });
  }, [applyPageStatus, send]);

  const selectDevice = useCallback(
    (next: DeviceKey) => {
      setDevice(next);
      setViewport(DEVICES[next]);
      send({ ...DEVICES[next], type: 'viewport' });
    },
    [send],
  );

  /**
   * @param opts `fullPage` shoots beyond the viewport, using Chromium's own
   *   capture-beyond-viewport. Defaults off; the walk never sets it — it
   *   stitches viewport tiles instead, see `stitch.ts`. `includeDom` defaults
   *   on; a stitch tile after the first skips the extraction, since only one
   *   snapshot is kept.
   */
  const capture = useCallback(
    (opts: { fullPage?: boolean; includeDom?: boolean } = {}) => {
      // Read from refs, not from the rendered values.
      //
      // `onCaptured` is called synchronously from the `captured` handler, one
      // line after `setCapturing(false)` — so anything it schedules is holding
      // a `capture` from a render where `capturing` was still true, and the
      // guard below turned that deferred call into a silent no-op. That is
      // exactly what an instant run does when a page is too long to shoot
      // whole: it asks for a second capture, got nothing, and sat there.
      //
      // Keeping the state as well as the ref is deliberate — the state is what
      // re-renders the button, the ref is what this decides on.
      if (capturingRef.current || pageStatusRef.current !== 'ready') return;
      captureIdRef.current += 1;
      capturingRef.current = true;
      setCapturing(true);
      setCaptureError(null);
      send({
        fullPage: opts.fullPage ?? false,
        includeDom: opts.includeDom ?? true,
        requestId: `c${String(captureIdRef.current)}`,
        type: 'capture',
      });
    },
    [send],
  );

  /**
   * One page-side step of a stitched capture, awaited. Rejects if the worker
   * answers with an error (an older worker that does not know the message
   * does exactly that) or the session closes first.
   */
  const stitch = useCallback(
    (
      type: BrowserStitchMessage['type'],
      y?: number,
    ): Promise<BrowserStitchState> =>
      new Promise<BrowserStitchState>((resolve, reject) => {
        const transport = transportRef.current;
        if (!transport) {
          reject(new Error('no browser session'));
          return;
        }
        stitchIdRef.current += 1;
        const requestId = `s${String(stitchIdRef.current)}`;
        stitchRequestsRef.current.set(requestId, { reject, resolve });
        transport.send({ requestId, type, y });
      }),
    [],
  );

  const scrollBy = useCallback(
    (deltaX: number, deltaY: number) => {
      send({ deltaX, deltaY, type: 'scroll' });
    },
    [send],
  );

  const retry = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  return useMemo(
    () => ({
      capture,
      captureError,
      capturing,
      device,
      endReason,
      failure,
      frame,
      goBack,
      goForward,
      navigate,
      pageError,
      pageStatus,
      quietFor,
      reload,
      retry,
      scrollBy,
      selectDevice,
      send,
      setLiveViewShown,
      status,
      stitch,
      url,
      viewport,
    }),
    [
      capture,
      captureError,
      capturing,
      device,
      endReason,
      failure,
      frame,
      goBack,
      goForward,
      navigate,
      pageError,
      pageStatus,
      quietFor,
      reload,
      retry,
      scrollBy,
      selectDevice,
      send,
      setLiveViewShown,
      status,
      stitch,
      url,
      viewport,
    ],
  );
}

export type BrowserSession = ReturnType<typeof useBrowserSession>;
